import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './AuthContext'
import { apiJson, ApiError, setActiveTenantId } from '../lib/api'
import { setBusinessZone } from '../pages/timesheets/time'
import { type Access, AccessContext, isAccess } from '../lib/access'
import { inviteTokenIn, type InviteOutcome } from '../lib/invite'
import { type ActingAs, actingFor, clearActAs, readActAs } from '../lib/actAs'

// Matches handoff.md §7b.1's CONTROL permission-set model exactly, and
// migration 041's tenant_memberships.role CHECK constraint - not the
// invented admin/supervisor/worker set the column used before its fix.
export interface Workspace {
  id: string
  name: string
  role: 'ADMIN' | 'OPERATIONS' | 'PAYROLL' | 'REPORTING' | 'SYSADMIN'
  // The tenant operating time zone (IANA), null while Dataphyll has not set
  // it. "Today" runs on it for every user (§9; TZ-1).
  timezone?: string | null
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null
  subscriptions: Workspace[]
  isLoading: boolean
  error: Error | null
  // True while the picker is showing over a workspace already chosen (the
  // sidebar brand block reopened it). The first pick needs no flag: with 2+
  // subscriptions and nothing chosen, currentWorkspace is simply null.
  pickerOpen: boolean
  selectWorkspace: (id: string) => void
  openPicker: () => void
  // Client Setup saved a new operating zone for this workspace: today moves
  // to it now, and a switch away and back re-adopts it, not the zone /api/me
  // gave at sign-in.
  noteTimezone: (id: string, timezone: string | null) => void
  // What the caller may reach in the current workspace, as the server
  // resolved it (lib/access.ts); null while not known. refreshAccess asks
  // again, for a screen that found the server's answer had changed, or that changed it (Client Setup).
  access: Access | null
  refreshAccess: () => void
  // Dataphyll staff, who see God Mode (routes/god-mode.js on the server).
  staff: boolean
  // Ask /api/me again without the loading screen: God Mode made a client,
  // and the staff member is now in it.
  reloadWorkspaces: () => void
  // Signed in at an invitation link (/invite/<token>): what taking it up came to. null elsewhere.
  invite: InviteOutcome | null
  // A SYSADMIN acting as a system user in the current workspace (lib/actAs.ts): whom. null otherwise.
  actingAs: ActingAs | null
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

// handoff §5: "Do not persist the selection past the session." sessionStorage
// is the session: a reload keeps the workspace, a closed tab or a sign-out
// does not. Never localStorage. The uid is stored with it so a different
// account signing into the same tab never inherits the last one's choice.
const SESSION_KEY = 'dp.workspace'

function readSessionChoice(uid: string): string | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw)
    return saved && saved.uid === uid && typeof saved.id === 'string' ? saved.id : null
  } catch {
    return null
  }
}

function writeSessionChoice(uid: string, id: string) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ uid, id }))
  } catch {
    // Storage blocked: the choice lasts until reload, which is still inside §5.
  }
}

function clearSessionChoice() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // Nothing stored to clear.
  }
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null)
  const [subscriptions, setSubscriptions] = useState<Workspace[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Whose /api/me the state above answers. Until it matches the signed-in
  // user the context reports loading, so the render between sign-in and the
  // effect starting cannot show NoAccessPage for an empty list.
  const [loadedUid, setLoadedUid] = useState<string | null>(null)
  // Access per workspace id: absent = not asked yet, null = asked and the
  // server sent none. /api/me answers for one workspace at a time.
  const [accessFor, setAccessFor] = useState<Record<string, Access | null>>({})
  const [accessTick, setAccessTick] = useState(0)
  const [staff, setStaff] = useState(false)
  // A reload after the first load keeps the screen up (no loading state).
  const [reloadTick, setReloadTick] = useState(0)
  const [invite, setInvite] = useState<InviteOutcome | null>(null)
  // A link is taken up once per page load, not again on every reload of /api/me.
  const triedInvite = useRef<string | null>(null)
  const { user, status } = useAuth()

  // Every API call reads the tenant from lib/api, so it is set before React
  // re-renders the pages: a page mounting for the new workspace can never
  // fetch with the old one's header. The business clock is set the same way,
  // so no page reads today in the previous workspace's zone.
  const adopt = useCallback((ws: Workspace | null) => {
    setActiveTenantId(ws ? ws.id : null)
    setBusinessZone(ws ? ws.timezone : null)
    setCurrentWorkspace(ws)
    setPickerOpen(false)
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadWorkspace = async () => {
      try {
        if (!reloadTick) setIsLoading(true)
        setError(null)

        // If not signed in, there is no workspace to load yet - AppRoutes
        // shows SignInPage in this state regardless of workspace context.
        if (status !== 'signed-in' || !user) {
          if (status === 'signed-out') {
            clearSessionChoice()
            clearActAs()
          }
          setSubscriptions([])
          setStaff(false)
          adopt(null)
          setLoadedUid(null)
          setIsLoading(false)
          return
        }

        // /api/me is asked without a workspace, so it lists every membership
        // rather than answering for whichever one was active before.
        setActiveTenantId(null)

        // An invitation link is taken up before /api/me, which would otherwise bind the invite by email
        // first (migration 164) and leave the link with nothing to claim. The workspace it joins is chosen.
        let invitedTo: string | null = null
        const token = inviteTokenIn(window.location.pathname)
        if (token && triedInvite.current !== token) {
          triedInvite.current = token
          try {
            const r = await apiJson<{ tenant_id: string }>(user, '/api/invite/claim', { body: { token } })
            invitedTo = r.tenant_id
            setInvite({ ok: true })
          } catch (err) {
            if (err instanceof ApiError && err.body?.code === 'INVITE_ALREADY_MEMBER') {
              invitedTo = err.body.tenant_id || null
              setInvite({ ok: true })
            } else {
              setInvite({ ok: false, message: err instanceof Error ? err.message : 'The invitation could not be taken up.' })
            }
          }
          if (cancelled) return
          if (invitedTo) writeSessionChoice(user.uid, invitedTo)
        }

        let data: any
        try {
          data = await apiJson(user, '/api/me')
        } catch (err) {
          if (err instanceof ApiError && err.status === 401) throw new Error('Unauthorized: Invalid or expired token')
          if (err instanceof ApiError && err.status === 403) throw new Error('No access: You do not have access to any workspace')
          throw err instanceof ApiError ? new Error(`API error: ${err.status}`) : err
        }
        if (cancelled) return
        const answered = typeof data?.workspace?.id === 'string' ? data.workspace.id : null
        // Asked without a workspace, so never as anyone: where this account acts as someone, that workspace's
        // access is asked again with the header (below), and its role is theirs.
        const acting = readActAs()
        const mine = acting && acting.uid === user.uid ? acting : null
        setAccessFor(answered && answered !== mine?.tenantId ? { [answered]: isAccess(data.access) ? data.access : null } : {})

        // An authenticated user with zero subscriptions is a real "no
        // access" state, not a reason to fabricate one - AppRoutes renders
        // NoAccessPage when currentWorkspace is null and subscriptions is
        // empty, which is exactly this case.
        const listed: Workspace[] = Array.isArray(data?.subscriptions) ? data.subscriptions : []
        const subs = (mine ? listed.map((w) => (w.id === mine.tenantId ? { ...w, role: mine.permission_set as Workspace['role'] } : w)) : [...listed])
          .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base', numeric: true }))
        // Alphabetical wherever workspaces are listed (picker, sidebar switcher), not the order the API found them in.
        setSubscriptions(subs)
        setStaff(data?.staff === true && !mine)
        if (subs.length === 1) {
          // §5: a single-subscription account never sees the picker.
          adopt(subs[0])
        } else if (subs.length >= 2) {
          // §5: 2+ means the picker, unless this same session already chose
          // one that is still a membership.
          const chosen = readSessionChoice(user.uid)
          adopt(subs.find((s) => s.id === chosen) || null)
        } else {
          adopt(null)
        }
      } catch (err) {
        if (cancelled) return
        // A failed workspace load is a real error state, not a reason to
        // fabricate a workspace - leaving subscriptions/currentWorkspace
        // empty routes to NoAccessPage/an explicit error, same as above.
        setSubscriptions([])
        setAccessFor({})
        setStaff(false)
        adopt(null)
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        if (!cancelled) {
          if (user && status === 'signed-in') setLoadedUid(user.uid)
          setIsLoading(false)
        }
      }
    }

    loadWorkspace()
    return () => {
      cancelled = true
    }
    // reloadTick is a dependency so reloadWorkspaces runs this again.
  }, [status, user, adopt, reloadTick])

  const reloadWorkspaces = useCallback(() => setReloadTick((t) => t + 1), [])

  // The chosen workspace's access, when /api/me first answered for another
  // one (2+ subscriptions), or when a screen asked for it again. Asked with
  // the chosen workspace's header, which adopt() has already set.
  const currentId = currentWorkspace?.id || null
  const known = currentId ? currentId in accessFor : true
  useEffect(() => {
    if (!currentId || known || !user || status !== 'signed-in') return
    let cancelled = false
    apiJson(user, '/api/me')
      .then((data: any) => {
        if (cancelled || data?.workspace?.id !== currentId) return
        setAccessFor((m) => ({ ...m, [currentId]: isAccess(data.access) ? data.access : null }))
      })
      .catch((err) => {
        // Acting as someone who may no longer be acted as (their sign-in went, or the SYSADMIN's did): stop, and
        // load again as yourself.
        if (err instanceof ApiError && String(err.body?.code || '').startsWith('ACT_AS_') && actingFor(user.uid, currentId)) {
          clearActAs()
          window.location.reload()
          return
        }
        // Not known: nothing is hidden, and the server still refuses.
        if (!cancelled) setAccessFor((m) => ({ ...m, [currentId]: null }))
      })
    return () => {
      cancelled = true
    }
  }, [currentId, known, user, status, accessTick])

  // Every workspace's is forgotten, not only this one's: God Mode's Client Setup changes another client's (its
  // Printable labels tick, its product and licence), and the menu there must show it when the staff member goes back.
  // Each is asked again only when its workspace is open.
  const refreshAccess = useCallback(() => {
    setAccessFor({})
    setAccessTick((t) => t + 1)
  }, [])

  const selectWorkspace = useCallback(
    (id: string) => {
      const ws = subscriptions.find((s) => s.id === id)
      // Only a listed membership can be chosen; the server would refuse any
      // other X-Tenant-Id with 403 anyway.
      if (!ws) return
      if (user) writeSessionChoice(user.uid, ws.id)
      adopt(ws)
    },
    [subscriptions, user, adopt]
  )

  const openPicker = useCallback(() => {
    if (subscriptions.length >= 2) setPickerOpen(true)
  }, [subscriptions.length])

  const noteTimezone = useCallback(
    (id: string, timezone: string | null) => {
      setSubscriptions((subs) => subs.map((s) => (s.id === id ? { ...s, timezone } : s)))
      if (currentWorkspace && currentWorkspace.id === id) {
        setBusinessZone(timezone)
        setCurrentWorkspace({ ...currentWorkspace, timezone })
      }
    },
    [currentWorkspace]
  )

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        subscriptions,
        isLoading: isLoading || (status === 'signed-in' && !!user && loadedUid !== user.uid),
        error,
        pickerOpen,
        selectWorkspace,
        openPicker,
        noteTimezone,
        access: currentId ? accessFor[currentId] ?? null : null,
        refreshAccess,
        staff,
        reloadWorkspaces,
        invite,
        actingAs: user && currentId ? actingFor(user.uid, currentId) : null,
      }}
    >
      <AccessContext.Provider value={currentId ? accessFor[currentId] ?? null : null}>{children}</AccessContext.Provider>
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider')
  }
  return context
}
